/* global gdAlertify */
import React, { useState, useEffect } from 'react';
import axiosInstance from '../../config/axiosConfig';
import { formatDate, formatMoney } from '../../utils/utils';
import GdTable from './GdTable';
import ModalBusquedaAuxiliar from './ModalBusquedaAuxiliar';
import SolicitudReemplazo2 from '../SolicitudReemplazo2';
import {
    formatAuxiliarSearchLabel,
    getNoEnviarPacienteStatus,
} from '../../utils/noEnviarPaciente';

// ─── Vista: Lista de solicitudes ─────────────────────────────────────────────
const VistaSolicitudes = ({ solicitudes, mostrarTodas, setMostrarTodas, onResolver, onEliminar }) => {
    const columns = [
        { name: 'id', title: 'Solicitud', width: 60 },
        { name: 'nombrePaciente', title: 'Paciente', width: 200 },
        { name: 'nombreAuxiliar', title: 'Auxiliar', width: 200 },
        { name: 'fechaInicio', title: 'Fecha Inicio', render: row => formatDate(row.fechaInicio), width: 100 },
        { name: 'turnoInicio', title: 'Inicio', render: row => row.turnoInicio === 'night' ? 'Noche' : 'Día', width: 70 },
        { name: 'fechaFin', title: 'Fecha Fin', render: row => formatDate(row.fechaFin), width: 100 },
        { name: 'turnoFin', title: 'Fin', render: row => row.turnoFin === 'day' ? 'Día' : 'Noche', width: 70 },
        { name: 'desTurno', title: 'Turno', width: 100 },
        {
            name: 'estado',
            title: 'Estado',
            render: (row) => row.estado == 1
                ? <span className="badge bg-success">Terminada</span>
                : <span className="badge bg-warning text-dark">Pendiente</span>,
            width: 90
        },
        {
            name: 'acciones',
            title: 'Acción',
            render: (row) => (
                <div className="d-flex gap-2">
                    {row.estado != 1 && (
                        <button className="btn btn-primary btn-sm" onClick={() => onResolver(row.id)}>
                            Resolver
                        </button>
                    )}
                    <button className="btn btn-danger btn-sm" onClick={() => onEliminar(row.id)}>
                        Eliminar
                    </button>
                </div>
            ),
            width: 180
        }
    ];

    return (
        <div>
            <div className="d-flex align-items-center gap-3 mb-3">
                <div className="form-check">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id="mostrarTodasModal"
                        checked={mostrarTodas}
                        onChange={(e) => setMostrarTodas(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="mostrarTodasModal">
                        Mostrar todas las solicitudes
                    </label>
                </div>
            </div>
            <GdTable
                columns={columns}
                data={solicitudes}
                defaultItemsPerPage={50}
                itemsPerPageOptions={[10, 25, 50, 100]}
                initialSort={[3, mostrarTodas ? 'desc' : 'asc']}
            />
        </div>
    );
};

// ─── Vista: Resolver solicitud ────────────────────────────────────────────────
const VistaResolver = ({ solicitudID, onVolver, onResuelto }) => {
    const [solicitud, setSolicitud] = useState({});
    const [slots, setSlots] = useState([]);
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [targetAuxiliarID, setTargetAuxiliarID] = useState(null);
    const [isAuxiliarModalOpen, setIsAuxiliarModalOpen] = useState(false);

    useEffect(() => {
        async function fetchData() {
            gdAlertify.showLoader();
            try {
                const response = await axiosInstance.get('/reemplazos/solicitud/ver/' + solicitudID);
                const solicitudInfo = response.data.data;
                setSolicitud(solicitudInfo);

                if (solicitudInfo.auxiliarID) {
                    setTargetAuxiliarID(parseInt(solicitudInfo.auxiliarID));
                }

                if (solicitudInfo.fechaInicio && solicitudInfo.fechaFin) {
                    const newSlots = generateDailySlots(
                        solicitudInfo.fechaInicio,
                        solicitudInfo.fechaFin,
                        solicitudInfo.auxiliar,
                        solicitudInfo.turnoID,
                        solicitudInfo.turnos || [],
                        solicitudInfo.turnoInicio || 'day',
                        solicitudInfo.turnoFin || 'night',
                        solicitudInfo
                    );
                    setSlots(newSlots);
                }
                gdAlertify.hideLoader();
            } catch (error) {
                gdAlertify.hideLoader();
                console.error('Error al obtener la solicitud: ', error);
            }
        }
        fetchData();
    }, [solicitudID]);

    const findRealShift = (dateStr, shiftType, fetchedShifts) => {
        return fetchedShifts.find(s => {
            if (s.fecha !== dateStr) return false;
            let sType = 0;
            if (s.tipo === 'day') sType = 1;
            else if (s.tipo === 'night') sType = 2;
            else if (s.tipoTurno) sType = parseInt(s.tipoTurno);
            return sType === shiftType;
        });
    };

    const generateDailySlots = (startStr, endStr, defaultOldAux, turnoID, fetchedShifts, turnoInicio, turnoFin, solicitudInfo) => {
        const generated = [];
        const [sy, sm, sd] = startStr.split('-').map(Number);
        const [ey, em, ed] = endStr.split('-').map(Number);
        const start = new Date(sy, sm - 1, sd, 12, 0, 0);
        const end = new Date(ey, em - 1, ed, 12, 0, 0);
        let current = new Date(start);

        const typesToGenerate = [{ id: 1, label: 'Día' }, { id: 2, label: 'Noche' }];
        const turnoToId = (turno) => turno === 'night' || turno === 2 || turno === '2' ? 2 : 1;
        const turnoInicioId = turnoToId(turnoInicio);
        const turnoFinId = turnoToId(turnoFin);

        const shouldIncludeSlot = (dateStr, shiftID) => {
            if (dateStr === startStr && shiftID < turnoInicioId) return false;
            if (dateStr === endStr && shiftID > turnoFinId) return false;
            return true;
        };

        while (current <= end) {
            const dateStr = current.toISOString().split('T')[0];
            typesToGenerate.forEach(type => {
                if (!shouldIncludeSlot(dateStr, type.id)) return;

                const realShift = findRealShift(dateStr, type.id, fetchedShifts);
                let oldAuxObj = null;

                if (realShift) {
                    if (realShift.reemplazoNombre) {
                        oldAuxObj = { id: realShift.reemplazoID, nombre: realShift.reemplazoNombre, apellidoPaterno: '', apellidoMaterno: '' };
                    } else if (realShift.auxiliarID && parseInt(realShift.auxiliarID) > 0 && realShift.auxiliarNombre) {
                        oldAuxObj = { id: parseInt(realShift.auxiliarID), nombre: realShift.auxiliarNombre, apellidoPaterno: '', apellidoMaterno: '' };
                    }
                }

                let displayName = '-';
                if (oldAuxObj?.nombre) {
                    displayName = oldAuxObj.apellidoPaterno
                        ? `${oldAuxObj.nombre} ${oldAuxObj.apellidoPaterno}`
                        : oldAuxObj.nombre;
                }

                let vTurno = solicitudInfo?.valorTurno || 0;
                let vColacion = solicitudInfo?.valorColacion || 0;
                let vLocomocion = solicitudInfo?.valorMovilizacion || 0;

                if (realShift) {
                    if (realShift.valorTurnoRt !== undefined) vTurno = realShift.valorTurnoRt;
                    if (realShift.valorColacionRt !== undefined) vColacion = realShift.valorColacionRt;
                    if (realShift.valorLocomocionRt !== undefined) vLocomocion = realShift.valorLocomocionRt;
                }

                generated.push({
                    date: new Date(current),
                    dateStr,
                    shiftLabel: type.label,
                    shiftID: type.id,
                    oldAuxiliar: oldAuxObj,
                    displayName,
                    newAuxiliar: null,
                    valorTurno: vTurno,
                    valorColacion: vColacion,
                    valorLocomocion: vLocomocion
                });
            });
            current.setDate(current.getDate() + 1);
        }
        return generated;
    };

    const isSlotEditable = (slot) => {
        if (!slot.oldAuxiliar || !slot.oldAuxiliar.id) return false;
        if (!targetAuxiliarID) return true;
        return slot.oldAuxiliar.id === targetAuxiliarID;
    };

    const toggleSlotSelection = (index) => {
        if (!isSlotEditable(slots[index])) return;
        setSelectedSlots(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const toggleAllSelection = () => {
        const editableIndices = slots
            .map((slot, idx) => ({ ...slot, idx }))
            .filter(s => isSlotEditable(s))
            .map(s => s.idx);
        const allSelected = editableIndices.length > 0 && editableIndices.every(idx => selectedSlots.includes(idx));
        setSelectedSlots(allSelected ? [] : editableIndices);
    };

    const handleSeleccionAuxiliar = (auxiliar) => {
        if (getNoEnviarPacienteStatus(auxiliar).active) {
            gdAlertify.notiError('No se puede seleccionar: NO ENVIAR A ESTE DOMICILIO.');
            return;
        }
        let blockedCount = 0;
        const updatedSlots = slots.map((slot, index) => {
            if (selectedSlots.includes(index) && isSlotEditable(slot)) {
                if (slot.newAuxiliar?.id === -999) { blockedCount++; return slot; }
                return { ...slot, newAuxiliar: auxiliar };
            }
            return slot;
        });
        setSlots(updatedSlots);
        setSelectedSlots([]);
        setIsAuxiliarModalOpen(false);
        if (blockedCount > 0) {
            gdAlertify.notify(`Se asignó el auxiliar, pero ${blockedCount} turno(s) "No Reemplazar" no se modificaron.`, 'warning');
        } else {
            gdAlertify.notify('Auxiliar asignado a los días seleccionados', 'success');
        }
    };

    const handleSkipSlot = (index) => {
        if (!isSlotEditable(slots[index])) return;
        const updatedSlots = [...slots];
        updatedSlots[index].newAuxiliar = { id: -999, nombre: 'NO REEMPLAZAR', apellidoPaterno: '' };
        setSlots(updatedSlots);
    };

    const handleClearSlot = (index) => {
        const updatedSlots = [...slots];
        updatedSlots[index].newAuxiliar = null;
        setSlots(updatedSlots);
    };

    const handleMassSkip = () => {
        if (!selectedSlots.length) return;
        const updatedSlots = [...slots];
        selectedSlots.forEach(index => {
            if (isSlotEditable(updatedSlots[index])) {
                updatedSlots[index].newAuxiliar = { id: -999, nombre: 'NO REEMPLAZAR', apellidoPaterno: '' };
            }
        });
        setSlots(updatedSlots);
        setSelectedSlots([]);
        gdAlertify.notify(`${selectedSlots.length} turnos marcados como "No Reemplazar"`, 'warning');
    };

    const handleMassClear = () => {
        if (!selectedSlots.length) return;
        const updatedSlots = [...slots];
        selectedSlots.forEach(index => {
            if (isSlotEditable(updatedSlots[index])) updatedSlots[index].newAuxiliar = null;
        });
        setSlots(updatedSlots);
        setSelectedSlots([]);
        gdAlertify.notify(`Se limpiaron ${selectedSlots.length} turnos`, 'success');
    };

    const handleOpenModal = () => {
        if (!selectedSlots.length) {
            gdAlertify.notiError('Seleccione al menos un día de la tabla.');
            return;
        }
        setIsAuxiliarModalOpen(true);
    };

    const getFormattedReemplazos = () => {
        return slots
            .filter(slot => slot.newAuxiliar !== null && slot.newAuxiliar.id !== -999)
            .map(slot => ({
                pacienteID: solicitud.pacienteID,
                auxiliarID: slot.oldAuxiliar ? slot.oldAuxiliar.id : null,
                auxiliarNuevoID: slot.newAuxiliar.id,
                fecha: slot.dateStr,
                periodo: slot.shiftID === 1 ? 'day' : 'night'
            }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const editableSlots = slots.filter(s => isSlotEditable(s));
        const unassignedSlots = editableSlots.filter(s => !s.newAuxiliar);

        if (unassignedSlots.length > 0) {
            const assigned = editableSlots.filter(s => s.newAuxiliar && s.newAuxiliar.id !== -999).length;
            const skipped = editableSlots.filter(s => s.newAuxiliar?.id === -999).length;
            gdAlertify.notiError(
                `No se puede guardar. Faltan ${unassignedSlots.length} turno(s) por completar.\n\n` +
                `✓ Asignados: ${assigned}\n⊘ No Reemplazar: ${skipped}\n✗ Pendientes: ${unassignedSlots.length}\n` +
                `━━━━━━━━━━━━━━━━\nTotal: ${editableSlots.length}`
            );
            return;
        }

        const reemplazosPayload = getFormattedReemplazos();
        const skippedCount = editableSlots.filter(s => s.newAuxiliar?.id === -999).length;

        const doSubmit = async (payload) => {
            gdAlertify.showLoader();
            try {
                const response = await axiosInstance.post('/auxiliar/reemplazo/new-crear', {
                    solicitudEnvio: payload,
                });
                if (!response.data.error) {
                    gdAlertify.notiSuccess(response.data.message);
                    onResuelto();
                } else {
                    gdAlertify.notiError(response.data.message);
                }
                gdAlertify.hideLoader();
            } catch (error) {
                gdAlertify.hideLoader();
                gdAlertify.notiError('Ocurrió un error al conectar con el servidor.');
            }
        };

        if (reemplazosPayload.length === 0 && skippedCount > 0) {
            gdAlertify.confirm({
                title: 'Todos los turnos marcados como "No Reemplazar"',
                message: `Todos los ${skippedCount} turnos fueron marcados como "No Reemplazar".\n\n¿Desea continuar y cerrar la solicitud sin asignar reemplazos?`,
                onConfirm: () => doSubmit({ ...solicitud, reemplazos: [] }),
                onCancel: () => gdAlertify.notify('Operación cancelada', 'info')
            });
            return;
        }

        const confirmMessage = skippedCount > 0
            ? `¿Desea confirmar la siguiente operación?\n\n✓ Reemplazos a asignar: ${reemplazosPayload.length}\n⊘ Turnos "No Reemplazar": ${skippedCount}\n━━━━━━━━━━━━━━━━━━━━━━━━\nTotal de turnos: ${editableSlots.length}`
            : `¿Desea confirmar ${reemplazosPayload.length} asignaciones de reemplazo?`;

        gdAlertify.confirm({
            title: 'Confirmar reemplazo',
            message: confirmMessage,
            onConfirm: () => doSubmit({ reemplazos: reemplazosPayload, ...solicitud }),
            onCancel: () => gdAlertify.notify('Envío cancelado', 'error')
        });
    };

    const editableCount = slots.filter(s => isSlotEditable(s)).length;
    const isAllSelected = editableCount > 0 && slots.every((s, idx) => !isSlotEditable(s) || selectedSlots.includes(idx));

    return (
        <div>
            {/* Info del paciente */}
            {solicitud.paciente && (
                <div className="d-flex justify-content-end mb-3 border-bottom pb-3 text-end text-muted">
                    <div>
                        <div>
                            <span className="fw-bold text-dark">
                                {solicitud.paciente.nombres} {solicitud.paciente.apellidoPaterno}
                            </span>
                        </div>
                        <div>
                            <small>
                                Rango: {formatDate(solicitud.fechaInicio)} ({solicitud.turnoInicio === 'night' ? 'Noche' : 'Día'}) / {formatDate(solicitud.fechaFin)} ({solicitud.turnoFin === 'day' ? 'Día' : 'Noche'})
                            </small>
                        </div>
                        {solicitud.auxiliar && (
                            <div className="badge bg-warning text-dark mt-1">
                                Reemplazando a: {solicitud.auxiliar.nombre} {solicitud.auxiliar.apellidoPaterno}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Barra de acciones */}
            <div className="d-flex gap-2 mb-3 bg-light p-3 rounded align-items-center border">
                <button className="btn btn-primary" onClick={handleOpenModal} disabled={!selectedSlots.length}>
                    <i className="fas fa-user-plus me-2"></i>
                    Asignar Reemplazo ({selectedSlots.length})
                </button>
                <button className="btn btn-outline-danger" onClick={handleMassSkip} disabled={!selectedSlots.length}>
                    <i className="fas fa-ban me-2"></i>
                    No Reemplazar
                </button>
                <button className="btn btn-outline-secondary" onClick={handleMassClear} disabled={!selectedSlots.length}>
                    <i className="fas fa-eraser me-2"></i>
                    Limpiar
                </button>
                <div className="flex-grow-1 text-end text-secondary">
                    <small>Solo puede reemplazar turnos asignados al auxiliar original de la solicitud.</small>
                </div>
            </div>

            <ModalBusquedaAuxiliar
                isOpen={isAuxiliarModalOpen}
                onClose={() => setIsAuxiliarModalOpen(false)}
                onSelect={handleSeleccionAuxiliar}
                pacienteAuxiliares={solicitud.paciente?.auxiliares || []}
                excludeAuxiliarID={targetAuxiliarID}
                pacienteID={solicitud.pacienteID || solicitud.paciente?.id}
                pacienteNombre={solicitud.paciente ? `${solicitud.paciente.nombres} ${solicitud.paciente.apellidoPaterno || ''}`.trim() : ''}
            />

            {/* Tabla de turnos */}
            <div className="table-responsive rounded border" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                <table className="table table-hover align-middle mb-0">
                    <thead className="table-white position-sticky top-0 shadow-sm" style={{ zIndex: 1 }}>
                        <tr>
                            <th style={{ width: '50px' }} className="text-center bg-light">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={isAllSelected}
                                    onChange={toggleAllSelection}
                                    disabled={editableCount === 0}
                                />
                            </th>
                            <th className="bg-light">Día</th>
                            <th className="bg-light">Fecha</th>
                            <th className="bg-light">Turno</th>
                            <th className="bg-light text-muted">Ausente (Original)</th>
                            <th className="bg-light text-success">Reemplazo (Nuevo)</th>
                            <th className="bg-light text-end">V. Turno</th>
                            <th className="bg-light text-end">V. Colación</th>
                            <th className="bg-light text-end pe-3">V. Locomoción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {slots.map((slot, index) => {
                            const editable = isSlotEditable(slot);
                            const textClass = slot.shiftID === 1 ? 'text-primary' : 'text-danger';
                            return (
                                <tr
                                    key={`${slot.dateStr}-${slot.shiftID}`}
                                    className={selectedSlots.includes(index) ? 'table-warning' : (editable ? '' : 'table-light text-muted')}
                                    style={!editable ? { opacity: 0.6 } : {}}
                                >
                                    <td className="text-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedSlots.includes(index)}
                                            onChange={() => toggleSlotSelection(index)}
                                            disabled={!editable}
                                        />
                                    </td>
                                    <td className={`fw-bold ${textClass} text-capitalize`}>
                                        {slot.date.toLocaleDateString('es-CL', { weekday: 'long' })}
                                    </td>
                                    <td className={textClass}>{formatDate(slot.dateStr)}</td>
                                    <td className={textClass}>{slot.shiftLabel}</td>
                                    <td>
                                        {slot.oldAuxiliar ? (
                                            <span className={`d-flex align-items-center gap-2 ${!editable ? 'text-decoration-line-through' : ''}`}>
                                                <i className={`fas ${editable ? 'fa-user-slash text-secondary' : 'fa-user-lock'}`}></i>
                                                {slot.displayName}
                                            </span>
                                        ) : '-'}
                                        {!editable && slot.oldAuxiliar && (
                                            <div style={{ fontSize: '0.75rem' }} className="text-danger fst-italic">
                                                No corresponde a solicitud
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ verticalAlign: 'middle' }}>
                                        {slot.newAuxiliar ? (
                                            <div className="d-flex align-items-center justify-content-between">
                                                {slot.newAuxiliar.id === -999 ? (
                                                    <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 px-3 py-2 rounded-pill">
                                                        <i className="fas fa-ban me-2"></i>No Reemplazar
                                                    </span>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2 flex-wrap text-success fw-bold">
                                                        <span className="d-flex align-items-center">
                                                            <i className="fas fa-user-check me-2"></i>
                                                            {formatAuxiliarSearchLabel(slot.newAuxiliar, {
                                                                includeRut: false,
                                                                includeNoEnviarSuffix: false,
                                                            })}
                                                        </span>
                                                        {getNoEnviarPacienteStatus(slot.newAuxiliar).active && (
                                                            <span className="badge bg-warning text-dark">
                                                                NO ENVIAR A ESTE DOMICILIO
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {editable && (
                                                    <button
                                                        className="btn btn-link text-danger p-0 ms-2"
                                                        onClick={() => handleClearSlot(index)}
                                                        title="Quitar asignación"
                                                        style={{ textDecoration: 'none' }}
                                                    >
                                                        <i className="fas fa-times-circle fa-lg"></i>
                                                    </button>
                                                )}
                                            </div>
                                        ) : editable ? (
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted small fst-italic ps-2">Sin Asignar</span>
                                                <button
                                                    className="btn btn-outline-secondary btn-sm border-0 bg-light text-muted"
                                                    onClick={() => handleSkipSlot(index)}
                                                    title='Marcar como "No Reemplazar"'
                                                >
                                                    <i className="fas fa-ban"></i>
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-danger small fst-italic">No Editable</span>
                                        )}
                                    </td>
                                    <td className="text-end font-monospace">{formatMoney(slot.valorTurno)}</td>
                                    <td className="text-end font-monospace">{formatMoney(slot.valorColacion)}</td>
                                    <td className="text-end font-monospace pe-3">{formatMoney(slot.valorLocomocion)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Footer con progreso */}
            {(() => {
                const editableSlots = slots.filter(s => isSlotEditable(s));
                const assignedSlots = editableSlots.filter(s => s.newAuxiliar && s.newAuxiliar.id !== -999);
                const skippedSlots = editableSlots.filter(s => s.newAuxiliar?.id === -999);
                const pendingSlots = editableSlots.filter(s => !s.newAuxiliar);
                const totalEditable = editableSlots.length;
                const completedSlots = assignedSlots.length + skippedSlots.length;
                const isComplete = pendingSlots.length === 0;
                const completionPercentage = totalEditable > 0 ? (completedSlots / totalEditable) * 100 : 0;

                return (
                    <div className="mt-3 pt-3 border-top">
                        <div className="mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-bold text-dark">Progreso de Asignación</span>
                                <span className={`badge ${isComplete ? 'bg-success' : 'bg-warning text-dark'}`}>
                                    {completedSlots} / {totalEditable} turnos completados ({completionPercentage.toFixed(0)}%)
                                </span>
                            </div>
                            <div className="progress" style={{ height: '20px' }}>
                                <div
                                    className="progress-bar bg-success"
                                    style={{ width: `${(assignedSlots.length / totalEditable) * 100}%` }}
                                >
                                    {assignedSlots.length > 0 && `${assignedSlots.length} Asignados`}
                                </div>
                                <div
                                    className="progress-bar bg-secondary"
                                    style={{ width: `${(skippedSlots.length / totalEditable) * 100}%` }}
                                >
                                    {skippedSlots.length > 0 && `${skippedSlots.length} No Reemplazar`}
                                </div>
                                <div
                                    className="progress-bar bg-danger"
                                    style={{ width: `${(pendingSlots.length / totalEditable) * 100}%` }}
                                >
                                    {pendingSlots.length > 0 && `${pendingSlots.length} Pendientes`}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex gap-3">
                                <div className="text-muted small">
                                    <i className="fas fa-check-circle text-success me-1"></i>
                                    <strong>{assignedSlots.length}</strong> Asignados
                                </div>
                                <div className="text-muted small">
                                    <i className="fas fa-ban text-secondary me-1"></i>
                                    <strong>{skippedSlots.length}</strong> No Reemplazar
                                </div>
                                {pendingSlots.length > 0 && (
                                    <div className="text-danger small">
                                        <i className="fas fa-exclamation-circle me-1"></i>
                                        <strong>{pendingSlots.length}</strong> Pendientes
                                    </div>
                                )}
                            </div>
                            <button
                                className={`btn ${isComplete ? 'btn-success' : 'btn-secondary'} px-4`}
                                onClick={handleSubmit}
                                disabled={!isComplete}
                                title={!isComplete ? 'Complete todos los turnos para guardar' : 'Guardar reemplazos'}
                            >
                                <i className="fas fa-save me-2"></i>
                                Guardar Reemplazos
                            </button>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

// ─── Modal principal ──────────────────────────────────────────────────────────
const ModalSolicitudesReemplazo = ({ isOpen, onClose, onRefresh }) => {
    const [solicitudes, setSolicitudes] = useState([]);
    const [mostrarTodas, setMostrarTodas] = useState(false);
    const [vistaActual, setVistaActual] = useState('lista'); // 'lista' | 'resolver' | 'crear'
    const [solicitudIDSeleccionada, setSolicitudIDSeleccionada] = useState(null);

    const fetchSolicitudes = async () => {
        try {
            const response = await axiosInstance.post('/reemplazos/solicitud/lista', {
                todas: mostrarTodas ? 1 : 0
            });
            setSolicitudes(response.data.error ? [] : (response.data.data || []));
        } catch (error) {
            console.error('Error al obtener solicitudes: ', error);
        }
    };

    useEffect(() => {
        if (isOpen) fetchSolicitudes();
    }, [isOpen, mostrarTodas]);

    const handleResolver = (id) => {
        setSolicitudIDSeleccionada(id);
        setVistaActual('resolver');
    };

    const handleVolver = () => {
        setVistaActual('lista');
        setSolicitudIDSeleccionada(null);
        fetchSolicitudes();
        if (onRefresh) onRefresh();
    };

    const handleResuelto = () => {
        setVistaActual('lista');
        setSolicitudIDSeleccionada(null);
        fetchSolicitudes();
        if (onRefresh) onRefresh();
    };

    const handleCreado = () => {
        setVistaActual('lista');
        fetchSolicitudes();
        if (onRefresh) onRefresh();
    };

    const handleEliminar = (id) => {
        gdAlertify.confirm({
            title: 'Eliminar Solicitud de reemplazo',
            message: '¿Está seguro de que desea eliminar esta Solicitud de reemplazo?',
            onConfirm: async () => {
                try {
                    const response = await axiosInstance.post('/reemplazos/solicitud/eliminar', { id });
                    if (!response.data.error) {
                        gdAlertify.notify('Solicitud de reemplazo eliminada correctamente', 'success');
                        fetchSolicitudes();
                    } else {
                        gdAlertify.notify(response.data.message || 'Error al eliminar la solicitud', 'error');
                    }
                } catch (error) {
                    gdAlertify.notify('Error al eliminar la solicitud', 'error');
                }
            },
            onCancel: () => gdAlertify.notify('Acción cancelada', 'info')
        });
    };

    const handleClose = () => {
        setVistaActual('lista');
        setSolicitudIDSeleccionada(null);
        onClose();
    };

    const tituloVista = {
        lista: 'Solicitudes de Reemplazo',
        resolver: `Resolver Solicitud #${solicitudIDSeleccionada}`,
        crear: 'Nueva Solicitud de Reemplazo',
    };

    if (!isOpen) return null;

    return (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div
                className="modal-dialog modal-dialog-scrollable"
                style={{ maxWidth: '90vw', width: '90vw', maxHeight: '90vh', margin: '5vh auto' }}
            >
                <div className="modal-content" style={{ maxHeight: '90vh' }}>
                    <div className="modal-header">
                        <h5 className="modal-title me-auto">
                            <i className="fas fa-exchange-alt me-2"></i>
                            {tituloVista[vistaActual]}
                        </h5>
                        <div className="d-flex align-items-center gap-2 me-2">
                            {vistaActual !== 'lista' && (
                                <button className="btn btn-outline-secondary btn-sm" onClick={handleVolver}>
                                    <i className="fas fa-arrow-left me-1"></i> Volver
                                </button>
                            )}
                            {vistaActual === 'lista' && (
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => setVistaActual('crear')}
                                >
                                    <i className="fas fa-plus me-1"></i> Crear Solicitud
                                </button>
                            )}
                        </div>
                        <button type="button" className="btn-close" onClick={handleClose}></button>
                    </div>
                    <div className="modal-body" style={{ overflowY: 'auto' }}>
                        {vistaActual === 'lista' && (
                            <VistaSolicitudes
                                solicitudes={solicitudes}
                                mostrarTodas={mostrarTodas}
                                setMostrarTodas={setMostrarTodas}
                                onResolver={handleResolver}
                                onEliminar={handleEliminar}
                            />
                        )}
                        {vistaActual === 'resolver' && (
                            <VistaResolver
                                solicitudID={solicitudIDSeleccionada}
                                onVolver={handleVolver}
                                onResuelto={handleResuelto}
                            />
                        )}
                        {vistaActual === 'crear' && (
                            <SolicitudReemplazo2 onCreado={handleCreado} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalSolicitudesReemplazo;
